import { ComponentPropsWithoutRef, ElementRef, forwardRef, ReactNode } from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '@utils'
import { cva, VariantProps } from 'class-variance-authority'

export type TooltipProps = TooltipPrimitive.TooltipProps &
  VariantProps<typeof tooltipVariants> & {
    content: ReactNode
    title?: ReactNode
    triggerProps?: TooltipPrimitive.TooltipTriggerProps
    side?: 'right' | 'top' | 'left' | 'bottom'
    className?: string
    contentClassName?: string
  }

export const tooltipVariants = cva('font-semibold mb-2', {
  variants: {
    variant: {
      default: 'bg-cw-purple-light font-normal text-cw-midnight-purple text-xs',
      dark: 'bg-cw-black-grey font-normal text-xs',
      grey: 'bg-cw-black-grey font-normal text-xs',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export function Tooltip({
  children,
  content,
  triggerProps,
  title,
  variant,
  side,
  className,
  contentClassName,
  ...props
}: TooltipProps) {
  return (
    <TooltipProvider {...props}>
      <TooltipRoot open={props.open}>
        <TooltipTrigger
          {...triggerProps}
          asChild={triggerProps?.asChild}
          aria-label={triggerProps?.['aria-label'] || 'Show tooltip'}
          onClick={(e) => {
            triggerProps?.onClick?.(e)
            if (!triggerProps?.asChild) {
              e.preventDefault()
            }
          }}
          className={cn(
            !triggerProps?.asChild &&
              'w-8 h-8 mt-[-4px] xs:w-auto xs:h-auto static xs:mt-0',
            className,
          )}
        >
          {children}
        </TooltipTrigger>
        <TooltipContent
          className={cn(tooltipVariants({ variant }), 'max-w-[325px]', contentClassName)}
          side={side}
        >
          {title && <div>{title}</div>}
          {content}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  )
}

export const TooltipProvider = TooltipPrimitive.Provider

export const TooltipRoot = TooltipPrimitive.Root

export const TooltipTrigger = TooltipPrimitive.Trigger

export const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'z-50 max-w-xl overflow-hidden whitespace-normal break-words rounded-md bg-cw-strong-grey px-3 py-1.5 text-body2 shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      className,
    )}
    {...props}
  />
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName
